/**
 * ROADMAP JOURNAL DISPLAY
 * 
 * Displays journal entries in a professional timeline format
 * for the Mining Operations tab in the shareholder dashboard.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RoadmapJournalService } from '../../lib/roadmapJournalService';
import { AnnouncementLayout, type UpdateBlock } from '../user/AnnouncementLayout';
import type { 
  RoadmapJournalEntry, 
  RoadmapJournalMedia,
  RoadmapJournalDisplayProps,
  LightboxState 
} from '../../types/roadmapJournal';

// Lightbox Component
const MediaLightbox: React.FC<{
  state: LightboxState;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}> = ({ state, onClose, onNext, onPrev }) => {
  if (!state.isOpen || state.media.length === 0) return null;

  const currentMedia = state.media[state.currentIndex];

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 z-10"
      >
        ×
      </button>
      
      {state.media.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl hover:text-gray-300 z-10"
          >
            ‹
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl hover:text-gray-300 z-10"
          >
            ›
          </button>
        </>
      )}

      <div className="max-w-6xl max-h-[90vh] w-full px-4" onClick={(e) => e.stopPropagation()}>
        {currentMedia.media_type === 'photo' ? (
          <img
            src={currentMedia.file_url}
            alt={currentMedia.caption || 'Journal photo'}
            className="max-w-full max-h-[85vh] mx-auto object-contain"
          />
        ) : (
          <video
            src={currentMedia.file_url}
            controls
            className="max-w-full max-h-[85vh] mx-auto"
          />
        )}
        
        {currentMedia.caption && (
          <p className="text-center text-white mt-4 text-sm">{currentMedia.caption}</p>
        )}
        
        {state.media.length > 1 && (
          <p className="text-center text-white mt-2 text-sm">
            {state.currentIndex + 1} / {state.media.length}
          </p>
        )}
      </div>
    </div>
  );
};

// Media Grid Component
const MediaGrid: React.FC<{
  media: RoadmapJournalMedia[];
  onMediaClick: (index: number) => void;
}> = ({ media, onMediaClick }) => {
  if (!media || media.length === 0) return null;

  const photos = media.filter(m => m.media_type === 'photo');
  const videos = media.filter(m => m.media_type === 'video');

  return (
    <div className="mt-4 space-y-4">
      {/* Photos Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((photo, _idx) => (
            <div
              key={photo.id}
              onClick={() => onMediaClick(media.indexOf(photo))}
              className="relative aspect-video bg-gray-700 rounded-lg overflow-hidden cursor-pointer 
                         hover:ring-2 hover:ring-yellow-400 transition-all group"
            >
              <img
                src={photo.file_url}
                alt={photo.caption || 'Journal photo'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-sm px-2 py-1 rounded">
                📷
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Videos Grid */}
      {videos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {videos.map((video, _idx) => (
            <div
              key={video.id}
              onClick={() => onMediaClick(media.indexOf(video))}
              className="relative aspect-video bg-gray-700 rounded-lg overflow-hidden cursor-pointer
                         hover:ring-2 hover:ring-yellow-400 transition-all group"
            >
              {video.thumbnail_url ? (
                <img
                  src={video.thumbnail_url}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                  <span className="text-4xl">🎥</span>
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-black/70 rounded-full flex items-center justify-center 
                              group-hover:bg-yellow-500/80 transition-colors">
                  <span className="text-white text-2xl ml-1">▶</span>
                </div>
              </div>
              {video.duration && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-sm px-2 py-1 rounded">
                  {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Journal Entry Card Component
const JournalEntryCard: React.FC<{
  entry: RoadmapJournalEntry;
  onMediaClick: (media: RoadmapJournalMedia[], index: number) => void;
  isFirst?: boolean;
}> = ({ entry, onMediaClick }) => {
  const { t } = useTranslation('common');
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isPast = new Date(entry.entry_date) <= new Date();

  return (
    <div className="relative pl-8 pb-8">
      {/* Timeline connector */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-yellow-400 to-gray-600" />

      {/* Timeline dot */}
      <div className={`absolute left-0 top-2 w-3 h-3 rounded-full -translate-x-1/2 border-2
        ${isPast ? 'bg-yellow-400 border-yellow-400' : 'bg-gray-600 border-gray-400'}`}
      />

      {/* Entry card */}
      <div className={`ml-4 bg-gradient-to-br ${
        isPast
          ? 'from-gray-700/80 to-gray-800/80 border-gray-600'
          : 'from-blue-900/30 to-gray-800/80 border-blue-600/30'
      } rounded-xl p-6 border shadow-lg hover:shadow-xl transition-shadow`}>
        {/* Date badge */}
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm mb-3 ${
          isPast
            ? 'bg-yellow-500/20 text-yellow-400'
            : 'bg-blue-500/20 text-blue-400'
        }`}>
          <span>{isPast ? '📅' : '🔮'}</span>
          <span className="font-medium">{formatDate(entry.entry_date)}</span>
          {!isPast && <span className="text-sm">({t('roadmap.upcoming', 'Upcoming')})</span>}
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-white mb-3">{entry.title}</h3>

        {Array.isArray(entry.blocks) && entry.blocks.length > 0 ? (
          <div className="mb-4">
            <AnnouncementLayout blocks={entry.blocks as UpdateBlock[]} />
          </div>
        ) : entry.description ? (
          <p className="text-gray-300 leading-relaxed whitespace-pre-wrap mb-4">
            {entry.description}
          </p>
        ) : null}

        {/* Media */}
        {entry.media && entry.media.length > 0 && (
          <MediaGrid
            media={entry.media}
            onMediaClick={(idx) => onMediaClick(entry.media!, idx)}
          />
        )}
      </div>
    </div>
  );
};

// Main Display Component
export const RoadmapJournalDisplay: React.FC<RoadmapJournalDisplayProps> = ({
  maxEntries = 20,
  showUnpublished = false
}) => {
  const { t } = useTranslation('common');
  const [entries, setEntries] = useState<RoadmapJournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<LightboxState>({
    isOpen: false,
    currentIndex: 0,
    media: []
  });

  useEffect(() => {
    loadEntries();
  }, [maxEntries, showUnpublished]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await RoadmapJournalService.getEntries({
        onlyPublished: !showUnpublished,
        includeMedia: true,
        orderBy: 'entry_date',
        orderDirection: 'desc',
        limit: maxEntries
      });
      setEntries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('roadmap.loadError', 'Failed to load journal'));
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (media: RoadmapJournalMedia[], index: number) => {
    setLightbox({ isOpen: true, currentIndex: index, media });
  };

  const closeLightbox = () => {
    setLightbox({ isOpen: false, currentIndex: 0, media: [] });
  };

  const nextMedia = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.media.length
    }));
  };

  const prevMedia = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: prev.currentIndex === 0 ? prev.media.length - 1 : prev.currentIndex - 1
    }));
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400" />
          <span className="ml-3 text-white">{t('roadmap.loading', 'Loading roadmap journal...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
        <p className="text-red-400">⚠️ {error}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="bg-gray-700/50 rounded-lg p-8 text-center">
        <div className="text-4xl mb-4">📝</div>
        <h3 className="text-lg font-semibold text-white mb-2">{t('roadmap.empty.heading', 'No Journal Entries Yet')}</h3>
        <p className="text-white">
          {t('roadmap.empty.body', 'Check back soon for updates on our mining operations progress.')}
        </p>
      </div>
    );
  }

  // Separate past and future entries
  const now = new Date();
  const pastEntries = entries.filter(e => new Date(e.entry_date) <= now);
  const futureEntries = entries.filter(e => new Date(e.entry_date) > now);

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-yellow-900/30 to-gray-800 rounded-lg p-6 border border-yellow-600/30">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">📖</span>
          <h3 className="text-xl font-bold text-white">{t('roadmap.heading', 'Roadmap Journal')}</h3>
        </div>
        <p className="text-gray-300">
          {t('roadmap.subheading', "Follow our journey as we build Africa's largest eco-friendly gold mining network. Each entry documents our progress, milestones, and plans for the future.")}
        </p>
      </div>

      {/* Past Updates */}
      {pastEntries.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
            <span>✅</span> {t('roadmap.progressUpdates', 'Progress Updates')}
          </h4>
          <div className="relative">
            {pastEntries.map((entry, idx) => (
              <JournalEntryCard
                key={entry.id}
                entry={entry}
                onMediaClick={openLightbox}
                isFirst={idx === 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* Future Plans */}
      {futureEntries.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-blue-400 mb-4 flex items-center gap-2">
            <span>🔮</span> {t('roadmap.upcomingMilestones', 'Upcoming Milestones')}
          </h4>
          <div className="relative">
            {futureEntries.map((entry, _idx) => (
              <JournalEntryCard
                key={entry.id}
                entry={entry}
                onMediaClick={openLightbox}
              />
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      <MediaLightbox
        state={lightbox}
        onClose={closeLightbox}
        onNext={nextMedia}
        onPrev={prevMedia}
      />
    </div>
  );
};

